/**
 * Client-side mapper for the PQR preview's Welding Variables matrix.
 *
 * Mirrors the read-side logic in `server/src/pdf/pqrDataMapper.ts`:
 *   - expand the active bevel's pass sequence (Root, Hot, Fill 1..N, Cap 1..M, Strip 1..K, customs)
 *   - for each (bevelIndex, passLabel), pick the matching `weldDetails.groups`
 *     entry with a four-step fallback chain (composite key → b0 composite →
 *     bare passLabel → legacy collapsed key).
 *   - per row, return either the raw field value or the composed Gas Flow
 *     Rate range; expose `min`/`max` separately so the editor can drive
 *     two-cell input UX without re-parsing strings.
 *
 * The mapper never mutates the input. The shape it returns is the snapshot
 * the diff/patch helpers consume, so a passes-by-rows matrix of plain strings
 * is enough.
 */

import { resolvePqrWeldingVariablePassColumns } from './floorSheetMisfirePrefill'
import {
  WELDING_VARIABLE_ROW_DEFS,
  composeMinMaxFieldValues,
  type WeldingVariableRowKey
} from './pqrWeldingVariableRowDefs'

export interface PqrEditableWeldingVariableCell {
  /** Resolved display value (composed for Gas Flow Rate, raw otherwise). */
  value: string
  /** Gas-flow-rate only — raw min cell. */
  min?: string
  /** Gas-flow-rate only — raw max cell. */
  max?: string
}

export interface PqrEditableWeldingVariableRow {
  rowKey: WeldingVariableRowKey
  label: string
  unit?: string
  /** Per-pass values, aligned with `passes`. */
  values: PqrEditableWeldingVariableCell[]
}

export interface PqrEditableWeldingVariables {
  /** Active bevel index (for patch payloads). */
  bevelIndex: number
  /** Expanded pass labels in PDF render order. */
  passes: string[]
  rows: PqrEditableWeldingVariableRow[]
}

const BASE_PASS_ORDER = ['Root', 'Hot', 'Fill', 'Cap', 'Strip', 'Root Misfire']
const MAX_FILL_COUNT = 21
const MAX_CAP_COUNT = 2

function clamp(n: number, max: number): number {
  return Math.min(max, Math.max(0, Math.trunc(n)))
}

function nonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function resolveActivePassPayload(
  passSection: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!passSection) return null
  const bevelPasses = passSection.bevelPasses
  if (Array.isArray(bevelPasses) && bevelPasses.length > 0) {
    const rawIdx = passSection.activeBevelIndex
    const idx =
      typeof rawIdx === 'number' && Number.isFinite(rawIdx)
        ? Math.trunc(rawIdx)
        : typeof rawIdx === 'string' && rawIdx.trim() !== '' && Number.isFinite(Number(rawIdx))
          ? Math.trunc(Number(rawIdx))
          : 0
    const clamped = Math.min(bevelPasses.length - 1, Math.max(0, idx))
    return asObject(bevelPasses[clamped])
  }
  return passSection
}

function resolveActiveBevelIndex(passSection: Record<string, unknown> | null): number {
  if (!passSection) return 0
  const raw = passSection.activeBevelIndex
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.trunc(raw))
  if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
    return Math.max(0, Math.trunc(Number(raw)))
  }
  return 0
}

function expandPassLabels(passSection: Record<string, unknown> | null): string[] {
  const payload = resolveActivePassPayload(passSection)
  if (!payload) return []
  const passSequence = Array.isArray(payload.passSequence)
    ? (payload.passSequence as unknown[]).filter(nonBlank)
    : []
  const selected = new Set(passSequence)
  const customPassesRaw = Array.isArray(payload.customPasses)
    ? (payload.customPasses as unknown[])
    : []
  const customPasses = customPassesRaw.map((item) => {
    if (typeof item === 'string') return { name: item, count: '1' }
    if (item && typeof item === 'object') {
      const obj = item as { name?: unknown; count?: unknown }
      return { name: String(obj.name ?? ''), count: String(obj.count ?? '1') }
    }
    return { name: '', count: '1' }
  })
  const order = [...BASE_PASS_ORDER]
  for (const p of customPasses) {
    if (p.name && !order.includes(p.name)) order.push(p.name)
  }
  const fillCount = clamp(Number(payload.fillCount ?? 0), MAX_FILL_COUNT)
  const capCount = clamp(Number(payload.capCount ?? 0), MAX_CAP_COUNT)
  const stripCount = Math.max(0, Math.trunc(Number(payload.stripCount ?? 0)))

  const list: string[] = []
  order.forEach((pass) => {
    if (!selected.has(pass)) return
    if (pass === 'Fill') {
      for (let i = 0; i < fillCount; i++) list.push(`Fill ${i + 1}`)
      return
    }
    if (pass === 'Cap') {
      for (let i = 0; i < capCount; i++) list.push(`Cap ${i + 1}`)
      return
    }
    if (pass === 'Strip') {
      for (let i = 0; i < stripCount; i++) list.push(`Strip ${i + 1}`)
      return
    }
    if (pass === 'Root Misfire') {
      const custom = customPasses.find((p) => p.name === pass)
      const count = Math.max(1, Math.trunc(Number(custom?.count || 1)))
      for (let i = 0; i < count; i++) list.push(`Root Misfire ${i + 1}`)
      return
    }
    const custom = customPasses.find((p) => p.name === pass)
    if (custom) {
      const count = Math.max(0, Math.trunc(Number(custom.count || 0)))
      for (let i = 0; i < count; i++) list.push(`${custom.name} ${i + 1}`)
      return
    }
    list.push(pass)
  })
  return list
}

function legacyKey(passLabel: string): string | null {
  if (/^Fill \d+$/.test(passLabel)) return 'Fill'
  if (/^Cap \d+$/.test(passLabel)) return 'Cap'
  if (/^Strip \d+$/.test(passLabel)) return 'Strip'
  const m = passLabel.match(/^(.+?) \d+$/)
  return m ? m[1] : null
}

function readGroupFields(
  groups: Record<string, unknown> | undefined,
  bevelIndex: number,
  passLabel: string
): Record<string, string> {
  if (!groups) return {}
  const candidates = [
    `b${bevelIndex}::${passLabel}`,
    bevelIndex !== 0 ? `b0::${passLabel}` : null,
    passLabel,
    legacyKey(passLabel)
  ].filter((c): c is string => !!c)
  for (const key of candidates) {
    const raw = groups[key]
    const obj = asObject(raw)
    if (!obj) continue
    const fields = asObject(obj.fields)
    if (!fields) continue
    const out: Record<string, string> = {}
    Object.entries(fields).forEach(([k, v]) => {
      out[k] = typeof v === 'string' ? v : ''
    })
    return out
  }
  return {}
}

/**
 * Read the welding variables matrix from `sections.weldDetails` + `sections.pass`,
 * returning a per-row × per-pass snapshot ready for the editor and the diff
 * builder.
 */
export function readWeldingVariablesFromSections(
  sections: Record<string, unknown> | null | undefined
): PqrEditableWeldingVariables {
  const safeSections = asObject(sections) ?? {}
  const passSection = asObject(safeSections.pass)
  const weldDetails = asObject(safeSections.weldDetails)
  const groups = asObject(weldDetails?.groups) ?? {}
  const expandedPasses = expandPassLabels(passSection)
  const passColumns = resolvePqrWeldingVariablePassColumns(expandedPasses)
  const passes = passColumns.map((col) => col.display)
  const bevelIndex = resolveActiveBevelIndex(passSection)

  const rows: PqrEditableWeldingVariableRow[] = WELDING_VARIABLE_ROW_DEFS.map((def) => {
    const values = passColumns.map<PqrEditableWeldingVariableCell>((col) => {
      const sources = col.sourcePasses?.length ? col.sourcePasses : [col.dataPass]
      if (def.composedFields?.length === 2) {
        const [minField, maxField] = def.composedFields
        const parts: string[] = []
        let firstMin = ''
        let firstMax = ''
        for (const pass of sources) {
          const fields = readGroupFields(groups, bevelIndex, pass)
          const composed = composeMinMaxFieldValues(fields, minField, maxField, def.rowKey)
          if (composed.value.trim()) parts.push(composed.value.trim())
          if (!firstMin && composed.min) firstMin = composed.min
          if (!firstMax && composed.max) firstMax = composed.max
        }
        const unique = Array.from(new Set(parts))
        const value = unique.join(' / ')
        return { value, min: firstMin, max: firstMax }
      }
      const parts: string[] = []
      for (const pass of sources) {
        const fields = readGroupFields(groups, bevelIndex, pass)
        const raw = def.field ? (fields[def.field] ?? '').trim() : ''
        if (raw) parts.push(raw)
      }
      const unique = Array.from(new Set(parts))
      return { value: unique.join(' / ') }
    })
    return { rowKey: def.rowKey, label: def.label, unit: def.unit, values }
  })

  return { bevelIndex, passes, rows }
}
